import React, { useRef, useEffect, useState } from 'react';
import './Canvas.css';
import { useImage } from '../../context/ImageContext';

const Canvas = ({ imageUrl, brushSize, eraserSize, activeButton, disabled = false }) => {
  const { imageCanvasRef, drawCanvasRef } = useImage();
  const contextRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState(null);
  const resolutionMultiplier = 2; // Increase canvas resolution by 2x

  // Set the cursor based on the selected tool
  useEffect(() => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;

    if (disabled) {
      canvas.style.cursor = 'default';
    } else if (activeButton === 'eraser') {
      canvas.style.cursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${eraserSize}' height='${eraserSize}' viewBox='0 0 ${eraserSize} ${eraserSize}'%3E%3Ccircle cx='${eraserSize / 2}' cy='${eraserSize / 2}' r='${eraserSize / 2}' fill='rgba(201, 194, 250, 0.8)' stroke='black' stroke-width='1'/%3E%3C/svg%3E") ${eraserSize / 2} ${eraserSize / 2}, auto`;
    } else if (activeButton === 'pen') {
      canvas.style.cursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${brushSize}' height='${brushSize}' viewBox='0 0 ${brushSize} ${brushSize}'%3E%3Ccircle cx='${brushSize / 2}' cy='${brushSize / 2}' r='${brushSize / 2}' fill='rgba(201, 194, 250, 0.8)' stroke='%23c9c2fa' stroke-width='1'/%3E%3C/svg%3E") ${brushSize / 2} ${brushSize / 2}, auto`;
    } else {
      canvas.style.cursor = 'default';
    }
  }, [activeButton, brushSize, eraserSize, disabled]);

  // Load the image into the background canvas and size both canvases equally
  useEffect(() => {
    const imageCanvas = imageCanvasRef.current;
    const drawCanvas = drawCanvasRef.current;
    const imageCtx = imageCanvas.getContext('2d', { alpha: false });
    const drawCtx = drawCanvas.getContext('2d');
    contextRef.current = drawCtx;

    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      const aspectRatio = img.width / img.height;

      const container = imageCanvas.parentElement;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      let displayWidth = containerWidth;
      let displayHeight = displayWidth / aspectRatio;

      if (displayHeight > containerHeight) {
        displayHeight = containerHeight;
        displayWidth = displayHeight * aspectRatio;
      }

      // Set canvas dimensions with resolution multiplier
      const canvasWidth = displayWidth * resolutionMultiplier;
      const canvasHeight = displayHeight * resolutionMultiplier;

      // Set canvas dimensions
      imageCanvas.width = drawCanvas.width = canvasWidth;
      imageCanvas.height = drawCanvas.height = canvasHeight;

      // Set display size
      imageCanvas.style.width = `${displayWidth}px`;
      imageCanvas.style.height = `${displayHeight}px`;
      drawCanvas.style.width = `${displayWidth}px`;
      drawCanvas.style.height = `${displayHeight}px`;

      // Enable image smoothing for better quality
      imageCtx.imageSmoothingEnabled = true;
      imageCtx.imageSmoothingQuality = 'high';
      drawCtx.imageSmoothingEnabled = true;
      drawCtx.imageSmoothingQuality = 'high';

      imageCtx.clearRect(0, 0, canvasWidth, canvasHeight);
      drawCtx.clearRect(0, 0, canvasWidth, canvasHeight);
      imageCtx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
    };
    img.onerror = (e) => {
      console.error('Error loading image:', e);
    };
  }, [imageUrl]);

  // Get (x, y) coordinates relative to the canvas
  const getCoordinates = (e) => {
    const canvas = drawCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;

    if (e.type.startsWith('touch')) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  // Start drawing or erasing
  const startDrawing = (e) => {
    if (e.type.startsWith('touch')) {
      e.preventDefault();
    }
    if (!contextRef.current || disabled) return;

    const ctx = contextRef.current;
    const { x, y } = getCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setLastPoint({ x, y });

    if (activeButton === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = eraserSize * resolutionMultiplier;
    } else if (activeButton === 'pen') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = '#c9c2fa';
      ctx.lineWidth = brushSize * resolutionMultiplier;
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setIsDrawing(true);
  };

  // Drawing movement
  const draw = (e) => {
    if (e.type.startsWith('touch')) {
      e.preventDefault();
    }
    if (!isDrawing || !contextRef.current || disabled) return;

    const ctx = contextRef.current;
    const { x, y } = getCoordinates(e);

    if (lastPoint) {
      const midPoint = {
        x: (lastPoint.x + x) / 2,
        y: (lastPoint.y + y) / 2,
      };

      ctx.quadraticCurveTo(lastPoint.x, lastPoint.y, midPoint.x, midPoint.y);
      ctx.stroke();
      setLastPoint(midPoint);
    } else {
      ctx.lineTo(x, y);
      ctx.stroke();
      setLastPoint({ x, y });
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setLastPoint(null);
    if (contextRef.current) {
      contextRef.current.closePath();
    }
  };

  // Add touch event handlers
  useEffect(() => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;

    // Add touch event listeners with { passive: false }
    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDrawing);
    canvas.addEventListener('touchcancel', stopDrawing);

    return () => {
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDrawing);
      canvas.removeEventListener('touchcancel', stopDrawing);
    };
  }, [isDrawing, activeButton, brushSize, eraserSize, disabled]);

  return (
    <div className="canvas-container" style={{ position: 'relative' }}>
      <canvas
        ref={imageCanvasRef}
        className="image-canvas"
        style={{ zIndex: 0 }}
      />
      <canvas
        ref={drawCanvasRef}
        className="drawing-canvas"
        style={{ zIndex: 1 }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseOut={stopDrawing}
      />
    </div>
  );
};

export default Canvas;
